import React, { useState, useEffect, useCallback } from 'react';
import { colors } from '../../utils/colors';
import type { Teacher, TeacherAttendanceRecord } from '../../utils/types';
import SlideToSubmit from '../../components/SlideToSubmit';
import MonthCalendar from '../../components/MonthCalendar';
import { markTeacherAttendance, getTeacherAttendanceList } from '../../Api/attendance';
import type { TeacherAttendanceEntry } from '../../Api/attendance';

interface Props {
  teacher: Teacher;
  record: TeacherAttendanceRecord | null;
  onSave: (record: TeacherAttendanceRecord) => void;
  onBack: () => void;
}

type Status = 'present' | 'absent' | 'late';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const todayISO = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD in IST

const WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS_EN = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

const formatDate = (iso: string) => {
  const d = new Date(iso + 'T00:00:00+05:30');
  return `${d.getDate()} ${MONTHS_EN[d.getMonth()]} ${d.getFullYear()}, ${WEEKDAYS[d.getDay()]}`;
};

const formatTime = (isoDatetime: string) => {
  // Always treat the server timestamp as UTC, then display in IST (UTC+5:30).
  // Handles all formats: with Z, with offset, with space separator, or naive (no tz info).
  const normalized = isoDatetime
    .replace(' ', 'T') // normalise space separator → T
    .trim();

  // If no timezone info present, assume UTC (append Z)
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(normalized);
  const utcString = hasTimezone ? normalized : normalized + 'Z';

  const d = new Date(utcString);

  return d.toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).toUpperCase();
};

// ─── Config ───────────────────────────────────────────────────────────────────

const OPTIONS: { value: Status; label: string; color: string; bg: string; border: string }[] = [
  { value: 'present', label: 'I am present', color: '#166534', bg: '#f0fdf4', border: '#86efac' },
  { value: 'late',    label: 'I am late',   color: '#854d0e', bg: '#fef9c3', border: '#facc15' },
   { value: 'absent',  label: 'On leave',   color: '#92400e', bg: '#fff7ed', border: '#fcd34d' },
];

const LOCKED_STYLE: Record<Status, { color: string; bg: string; border: string }> = {
  present: { color: '#166534', bg: '#f0fdf4', border: '#86efac' },
   late:  { color: '#854d0e', bg: '#fef9c3', border: '#facc15' },
   absent: { color: '#92400e', bg: '#fff7ed', border: '#fcd34d' },
};

const STATUS_LABEL: Record<Status, string> = {
  present: 'Present',
  late:    'Late',
  absent:  'On Leave',
};

// ─── Component ────────────────────────────────────────────────────────────────

const TeacherAttendance: React.FC<Props> = ({ teacher, onSave, onBack }) => {
  const [selectedDate, setSelectedDate] = useState(todayISO());
  const [entry,        setEntry]        = useState<TeacherAttendanceEntry | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [selected,     setSelected]     = useState<Status | null>(null);
  const [saving,       setSaving]       = useState(false);
  const [saveError,    setSaveError]    = useState('');
  const [showCalendar, setShowCalendar] = useState(false);

  const isToday  = selectedDate === todayISO();
  const isFuture = selectedDate > todayISO();

  const fetchAttendance = useCallback(async (date: string) => {
    setLoading(true);
    setEntry(null);
    setSelected(null);
    setSaveError('');
    try {
      const list = await getTeacherAttendanceList({ teacher_id: Number(teacher.id), date });
      const found = list.find(e => e.date === date) ?? null;
      setEntry(found);
      if (found) setSelected(found.status);
    } catch {
      // network error — treat as no record
    }
    setLoading(false);
  }, [teacher.id]);

  useEffect(() => { fetchAttendance(selectedDate); }, [selectedDate, fetchAttendance]);

  const handleConfirm = async () => {
    if (!selected) return;
    setSaving(true);
    setSaveError('');
    try {
      const res = await markTeacherAttendance({
        teacher_id: Number(teacher.id),
        date: selectedDate,
        status: selected,
        note: null,
      });
      setEntry(res.data);
      onSave({ date: res.data.date, status: res.data.status, markedAt: res.data.created_at });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save. Please try again.');
    }
    setSaving(false);
  };

  const lockedStyle = entry ? LOCKED_STYLE[entry.status] : null;

  return (
    <div style={{
      height: '100dvh',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
    }}>
      {/* ── Fixed header ── */}
      <div style={{
        padding: '30px 20px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.borderLight}`,
        flexShrink: 0,
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 16, fontWeight: 500, color: colors.text,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          My attendance
        </button>

        <button
          onClick={() => setShowCalendar(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 6 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke={colors.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8"  y1="2" x2="8"  y2="6"/>
            <line x1="3"  y1="10" x2="21" y2="10"/>
            <rect x="8" y="14" width="2" height="2" rx="0.5" fill={colors.primary}/>
          </svg>
        </button>
      </div>

      {/* ── Scrollable body ── */}
      <div style={{
        flex: 1,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: '20px 20px 140px',
        display: 'flex', flexDirection: 'column', gap: 12,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700, color: colors.textMuted,
          letterSpacing: '0.8px', marginBottom: 4,
        }}>
          LOG ATTENDANCE
        </div>

        {/* Date pill */}
        <div style={{
          background: colors.primaryDark, borderRadius: 12,
          padding: '13px 18px', fontSize: 15, fontWeight: 600, color: '#fff',
        }}>
          {isToday ? `Today - ${formatDate(selectedDate)}` : formatDate(selectedDate)}
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 0' }}>
            <div style={{
              width: 30, height: 30,
              border: `3px solid ${colors.borderLight}`,
              borderTopColor: colors.primary,
              borderRadius: '50%',
              animation: 'spin 0.7s linear infinite',
            }} />
          </div>

        ) : isFuture ? (
          <div style={{
            background: colors.primaryBg,
            border: `1.5px solid ${colors.borderLight}`,
            borderRadius: 14, padding: '28px 18px',
            textAlign: 'center', marginTop: 8,
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: colors.textMuted }}>
              Cannot mark future attendance
            </div>
          </div>

        ) : entry ? (
          <div style={{
            background: lockedStyle?.bg ?? '#f8fafc',
            borderRadius: 14, padding: '20px 18px',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 20, fontWeight: 800, color: lockedStyle?.color ?? colors.text }}>
                {STATUS_LABEL[entry.status]}
              </span>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: lockedStyle?.color ?? colors.text,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
            </div>
            <div style={{ fontSize: 14, fontWeight: 500, color: colors.text }}>
              Log time &ndash; {formatTime(entry.created_at)}
            </div>
          </div>

        ) : isToday ? (
          <>
            {OPTIONS.map(opt => {
              const active = selected === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => setSelected(opt.value)}
                  style={{
                    width: '100%', padding: '16px 18px',
                    background: active ? opt.bg : '#fff',
                    border: `1.5px solid ${active ? opt.border : colors.border}`,
                    borderRadius: 14,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  <span style={{ fontSize: 16, fontWeight: 600, color: active ? opt.color : colors.textMuted }}>
                    {opt.label}
                  </span>
                  <div style={{
                    width: 24, height: 24, borderRadius: 6,
                    border: `2px solid ${active ? opt.color : colors.border}`,
                    background: active ? opt.color : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0, transition: 'all 0.15s',
                  }}>
                    {active && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                        stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </div>
                </button>
              );
            })}

            {saveError && (
              <div style={{
                background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`,
                borderRadius: 10, padding: '10px 14px',
                fontSize: 13, color: colors.danger, textAlign: 'center',
              }}>
                {saveError}
              </div>
            )}
          </>

        ) : (
          <div style={{
            background: '#f8fafc',
            border: `1.5px solid ${colors.border}`,
            borderRadius: 14, padding: '28px 18px',
            textAlign: 'center', marginTop: 8,
          }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: colors.textMuted }}>
              No attendance record for this date
            </div>
          </div>
        )}
      </div>

      {/* ── Fixed bottom slide-to-confirm (today only, no entry yet) ── */}
      {!loading && isToday && !entry && (
        <div style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          zIndex: 20, pointerEvents: 'none',
        }}>
          <div style={{
            height: 48,
            background: 'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
            pointerEvents: 'none',
          }} />
          <div style={{ background: '#fff', padding: '0 20px 32px', pointerEvents: 'all' }}>
            <SlideToSubmit
              onConfirm={handleConfirm}
              disabled={!selected || saving}
              label="Slide to confirm"
              confirmedLabel="Attendance Saved!"
            />
          </div>
        </div>
      )}

      {/* ── Calendar bottom sheet ── */}
      <MonthCalendar
        visible={showCalendar}
        onClose={() => setShowCalendar(false)}
        selectedDate={selectedDate}
        onSelectDate={date => { setSelectedDate(date); setShowCalendar(false); }}
      />
    </div>
  );
};

export default TeacherAttendance;